import { Component, OnInit, Input } from "@angular/core";
import { ModalController } from "@ionic/angular";
import {
  Storage,
  ref,
  listAll,
  getMetadata,
  StorageReference,
} from "@angular/fire/storage";

import { SelectedFile } from "@shared/interfaces/firmware.interface";

const DISPLAY_NAMES: Record<string, string> = {
  2: "RCS",
  3: "Standalone",
  4: "MIC 4.0",
  5: "RCS II",
  0: "Firmwares",
};

const FOLDER_ACCESS: Record<string, string[]> = {
  "2/0/protected": ["superadmin"],
  "3/0/protected": ["superadmin"],
  "4/0/protected": ["superadmin"],
  "5/0/protected": ["superadmin"],
};

export interface TreeNode {
  name: string; // Firebase storage folder/file name
  displayName: string; // Human-readable label
  fullPath: string; // Full path from storage root
  type: "folder" | "file";
  ref: StorageReference;
  children?: TreeNode[];
  fileCount?: number;
  size?: string;
  timeCreated?: string;
  contentType?: string;
  accessible: boolean;
  expanded?: boolean;
}

@Component({
  selector: "app-file-picker",
  templateUrl: "./file-picker.component.html",
  styleUrl: "./file-picker.component.scss",
})
export class FilePickerComponent {
  @Input() storagePath: string = ""; // Root path to list from, e.g. '' or 'company_files'
  @Input() title: string = "Select a Document";
  @Input() subtitle: string = "Browse and select a file";
  @Input() userRole!: string;

  public tree: TreeNode[] = [];
  public currentPath: string[] = []; // Navigation breadcrumb path (array of node names)
  public currentChildren: TreeNode[] = [];
  public selectedFile: SelectedFile | null = null;
  public searchQuery: string = "";
  public searchResults: TreeNode[] = [];
  public isSearching: boolean = false;
  public isLoading: boolean = true;

  constructor(
    private modalCtrl: ModalController,
    private storage: Storage,
  ) {}

  async ngOnInit() {
    console.log("role", this.userRole);
    await this.loadTree();
    this.updateCurrentView();
    this.isLoading = false;
  }

  async loadTree() {
    const rootRef = ref(this.storage, this.storagePath);
    this.tree = await this.buildTreeNode(rootRef);
  }

  private async buildTreeNode(
    storageRef: StorageReference,
  ): Promise<TreeNode[]> {
    const result = await listAll(storageRef);
    const nodes: TreeNode[] = [];

    // Process sub-folders
    for (const folderRef of result.prefixes) {
      const accessible = this.canAccess(folderRef.fullPath);
      const node: TreeNode = {
        name: folderRef.name,
        displayName: DISPLAY_NAMES[folderRef.name] ?? folderRef.name,
        fullPath: folderRef.fullPath,
        type: "folder",
        ref: folderRef,
        children: [],
        fileCount: 0,
        accessible,
        expanded: false,
      };

      if (accessible) {
        node.children = await this.buildTreeNode(folderRef);
        node.fileCount = this.countFiles(node);
      }

      nodes.push(node);
    }

    // Process files
    for (const fileRef of result.items) {
      try {
        const meta = await getMetadata(fileRef);
        nodes.push({
          name: fileRef.name,
          displayName: fileRef.name,
          fullPath: fileRef.fullPath,
          type: "file",
          ref: fileRef,
          accessible: true,
          size: this.formatBytes(meta.size),
          timeCreated: this.formatDate(meta.timeCreated),
          contentType: meta.contentType ?? "",
        });
      } catch {
        // File metadata failed — skip gracefully
      }
    }

    return nodes;
  }

  // ─── ACCESS CONTROL ──────────────────────────────────────────────────────────

  private toRelativePath(fullPath: string): string {
    const root = (this.storagePath ?? "").replace(/^\/|\/$/g, ""); // trim / both ends
    const path = fullPath.replace(/^\/|\/$/g, ""); // trim / both ends

    if (!root) return path;
    return path.startsWith(root + "/") ? path.slice(root.length + 1) : path;
  }
  /*
  canAccess(fullPath: string): boolean {
    // Strip leading storagePath prefix if present for config lookup
    const lookupPath = this.storagePath
      ? fullPath.replace(new RegExp(`^${this.storagePath}/?`), "")
      : fullPath;

    const allowedRoles = FOLDER_ACCESS[lookupPath];
    if (allowedRoles === undefined || allowedRoles === null) return true; // accessible to all
    return allowedRoles.includes(this.userRole);
  }
*/

  canAccess(fullPath: string): boolean {
    const relativePath = this.toRelativePath(fullPath);

    const isProtected = relativePath.split("/").includes("protected");
    if (!isProtected) return true;

    return this.userRole === "superadmin";
  }
  // ─── NAVIGATION ──────────────────────────────────────────────────────────────

  updateCurrentView() {
    if (this.currentPath.length === 0) {
      this.currentChildren = this.tree;
    } else {
      let nodes = this.tree;
      for (const name of this.currentPath) {
        const found = nodes.find((n) => n.name === name);
        nodes = found?.children ?? [];
      }
      this.currentChildren = nodes;
    }
  }

  enterFolder(node: TreeNode) {
    if (!node.accessible) return;
    this.currentPath = [...this.currentPath, node.name];
    // Expand in sidebar too
    this.expandInTree(this.tree, node.fullPath);
    this.updateCurrentView();
  }

  private expandInTree(nodes: TreeNode[], targetPath: string) {
    for (const n of nodes) {
      if (n.type === "folder" && targetPath.startsWith(n.fullPath)) {
        n.expanded = true;
        if (n.children) this.expandInTree(n.children, targetPath);
      }
    }
  }

  navigateToIndex(index: number) {
    // index -1 = root, 0 = first crumb, etc.
    this.currentPath = index < 0 ? [] : this.currentPath.slice(0, index + 1);
    this.updateCurrentView();
  }

  navigateViaSidebar(node: TreeNode) {
    if (!node.accessible) return;
    // Build path from root to this node
    const path = this.getPathToNode(this.tree, node.fullPath, []);
    if (path) {
      this.currentPath = path;
      node.expanded = !node.expanded;
      this.updateCurrentView();
    }
  }

  private getPathToNode(
    nodes: TreeNode[],
    targetPath: string,
    acc: string[],
  ): string[] | null {
    for (const n of nodes) {
      if (n.fullPath === targetPath) return [...acc, n.name];
      if (n.children) {
        const result = this.getPathToNode(n.children, targetPath, [
          ...acc,
          n.name,
        ]);
        if (result) return result;
      }
    }
    return null;
  }

  toggleSidebarExpand(node: TreeNode, event: Event) {
    event.stopPropagation();
    if (node.accessible) node.expanded = !node.expanded;
  }

  // ─── BREADCRUMB ───────────────────────────────────────────────────────────────

  get breadcrumbs(): { label: string; index: number }[] {
    const crumbs = [{ label: "Firmwares", index: -1 }];
    let nodes = this.tree;
    this.currentPath.forEach((name, i) => {
      const node = nodes.find((n) => n.name === name);
      crumbs.push({ label: node?.displayName ?? name, index: i });
      nodes = node?.children ?? [];
    });
    return crumbs;
  }

  // ─── FILE SELECTION ───────────────────────────────────────────────────────────

  selectFile(node: TreeNode) {
    if (this.selectedFile?.fullPath === node.fullPath) {
      this.selectedFile = null;
      return;
    }
    // Build human-readable path
    let nodes = this.tree;
    const pathLabels: string[] = [];
    for (const name of this.currentPath) {
      const found = nodes.find((n) => n.name === name);
      pathLabels.push(found?.displayName ?? name);
      nodes = found?.children ?? [];
    }

    this.selectedFile = {
      fullPath: node.fullPath,
      name: node.name,
      displayPath: [...pathLabels, node.name].join(" › "),
    };
  }

  confirmSelection() {
    if (!this.selectedFile) return;
    this.modalCtrl.dismiss({ file: this.selectedFile }, "confirm");
  }

  dismiss() {
    this.modalCtrl.dismiss(null, "cancel");
  }

  clearSelection() {
    this.selectedFile = null;
  }

  // ─── SEARCH ───────────────────────────────────────────────────────────────────

  onSearchChange(value: string) {
    this.searchQuery = value.trim().toLowerCase();
    this.isSearching = !!this.searchQuery;
    if (this.isSearching) {
      this.searchResults = this.flatSearch(this.tree, this.searchQuery);
    }
  }

  private flatSearch(nodes: TreeNode[], query: string): TreeNode[] {
    const results: TreeNode[] = [];
    for (const n of nodes) {
      if (
        n.type === "file" &&
        n.accessible &&
        n.name.toLowerCase().includes(query)
      ) {
        results.push(n);
      }
      if (n.children) results.push(...this.flatSearch(n.children, query));
    }
    return results;
  }

  // ─── CURRENT VIEW HELPERS ─────────────────────────────────────────────────────

  get currentFolders(): TreeNode[] {
    return this.currentChildren.filter((n) => n.type === "folder");
  }

  get currentFiles(): TreeNode[] {
    return this.currentChildren.filter((n) => n.type === "file");
  }

  isSelected(node: TreeNode): boolean {
    return this.selectedFile?.fullPath === node.fullPath;
  }

  fileIcon(contentType: string = ""): string {
    if (contentType.includes("pdf")) return "document-text-outline";
    if (contentType.includes("sheet") || contentType.includes("excel"))
      return "grid-outline";
    if (contentType.includes("word") || contentType.includes("document"))
      return "document-outline";
    if (contentType.includes("image")) return "image-outline";
    if (contentType.includes("video")) return "videocam-outline";
    if (contentType.includes("zip") || contentType.includes("compressed"))
      return "archive-outline";
    return "attach-outline";
  }

  fileIconColor(contentType: string = ""): string {
    if (contentType.includes("pdf")) return "danger";
    if (contentType.includes("sheet") || contentType.includes("excel"))
      return "success";
    if (contentType.includes("word") || contentType.includes("document"))
      return "primary";
    if (contentType.includes("image")) return "tertiary";
    return "medium";
  }

  // ─── UTILS ───────────────────────────────────────────────────────────────────

  private countFiles(node: TreeNode): number {
    if (!node.children) return 0;
    return node.children.reduce((acc, n) => {
      return acc + (n.type === "file" ? 1 : this.countFiles(n));
    }, 0);
  }

  private formatBytes(bytes: number): string {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(0) + " KB";
    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  }

  private formatDate(iso: string): string {
    return new Date(iso).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  }

  trackByPath(_: number, node: TreeNode) {
    return node.fullPath;
  }
}
